/**
 * Draw the heading numbers, without putting them in the document.
 *
 * A DECORATION, NOT TEXT. If "4.2" were typed into the heading it would be text somebody could
 * edit, text that search would match, text the renderer would print a second number beside, and
 * text that goes stale the moment a section is inserted above it. As a decoration it is drawn from
 * the outline every time and cannot be wrong.
 *
 * THE NUMBER COMES FROM outline.ts, which is the same rule document_rendition.py mirrors. Nothing
 * here computes anything: a second place that works out section numbers is a second place to get
 * them different from the PDF.
 *
 * RECOMPUTED ON EVERY DOCUMENT CHANGE, which sounds expensive and is not: the walk is over the
 * document's TOP-LEVEL children only, so it is proportional to the number of blocks, not to the
 * amount of text in them.
 */
import { Extension } from "@tiptap/core";
import { Plugin, PluginKey } from "@tiptap/pm/state";
import { Decoration, DecorationSet } from "@tiptap/pm/view";

import { MAX_NUMBERED_LEVEL } from "./outline";

const key = new PluginKey("outlineNumbers");

/**
 * Number the headings of one document node.
 *
 * DELIBERATELY NOT outlineOf(): that one walks a JSON body, and this walks a live ProseMirror
 * document where positions matter. THE RULE IS IDENTICAL and is stated in both places: close every
 * level deeper than this heading, then either take the next sibling number or open one step
 * deeper -- however far the author jumped. Tracking depth alone made two h3s under an h1 nest
 * instead of being siblings.
 */
function numbers(doc: any): { pos: number; size: number; number: string }[] {
    const out: { pos: number; size: number; number: string }[] = [];
    const counters: number[] = [];
    const openLevels: number[] = [];

    doc.forEach((node: any, offset: number) => {
        if (node.type.name !== "heading") return;
        const raw = Math.max(1, Math.min(MAX_NUMBERED_LEVEL, Number(node.attrs?.level) || 1));

        while (openLevels.length && openLevels[openLevels.length - 1] > raw) {
            openLevels.pop();
            counters.pop();
        }
        if (openLevels.length && openLevels[openLevels.length - 1] === raw) {
            counters[counters.length - 1] += 1;
        } else {
            openLevels.push(raw);
            counters.push(1);
        }

        out.push({ pos: offset, size: node.nodeSize, number: counters.join(".") });
    });

    return out;
}

export const OutlineNumbers = Extension.create({
    name: "outlineNumbers",

    addProseMirrorPlugins() {
        return [new Plugin({
            key,
            props: {
                decorations(state) {
                    // OFF UNLESS THE DOCUMENT ASKS FOR IT. Numbering every existing procedure the
                    // day this shipped would change what all of them print, with nothing in any
                    // record saying why.
                    if (!state.doc.attrs?.outlineNumbering) return DecorationSet.empty;
                    return DecorationSet.create(state.doc, numbers(state.doc).map(
                        ({ pos, size, number }) =>
                            Decoration.node(pos, pos + size, { "data-number": number })));
                },
            },
        })];
    },
});
